import { createSelector } from 'reselect';
import type { CustomerCardStoreState } from '../types/CustomerCardTypes';
import {
    CARD_OPERATION_EXCLUDES_DEAL_PARTICIPANTS,
    CARD_OPERATION_OWNS_CARD,
    CARD_OPERATION_STEP,
    CARD_OPERATION_TARGET
} from '../constants';
import { getSearchResultId } from '../utils/search';

const getSlice = (rootState: CustomerCardStoreState) => rootState.customerCard;

export const getCustomerCardState = getSlice;

export const getBridgeUsername = (rootState: CustomerCardStoreState): string => rootState.bridgeUser?.username || '';

export const getBuyer = (rootState: CustomerCardStoreState) => getSlice(rootState)?.buyer;

export const getCoBuyer = (rootState: CustomerCardStoreState) => getSlice(rootState)?.coBuyer ?? null;

export const getHasCoBuyer = (rootState: CustomerCardStoreState) => getSlice(rootState)?.hasCoBuyer ?? false;

export const getInitialTab = (rootState: CustomerCardStoreState) => getSlice(rootState)?.initialTab;

export const getSaveStatus = (rootState: CustomerCardStoreState) => getSlice(rootState)?.saveStatus ?? 'idle';

export const getIsSaving = (rootState: CustomerCardStoreState) => getSlice(rootState)?.saveStatus === 'saving';

export const getSaveError = (rootState: CustomerCardStoreState) => getSlice(rootState)?.saveError;

export const getCreditConsentJustGranted = (rootState: CustomerCardStoreState) =>
    getSlice(rootState)?.creditConsentJustGranted ?? false;

export const getSaveParticipantsError = (rootState: CustomerCardStoreState) => getSlice(rootState)?.saveParticipantsError;

export const getIsSwapping = (rootState: CustomerCardStoreState) => getSlice(rootState)?.swapStatus === 'saving';

export const getSwapFailed = (rootState: CustomerCardStoreState) => getSlice(rootState)?.swapStatus === 'error';

export const getSwapSucceeded = (rootState: CustomerCardStoreState) => getSlice(rootState)?.swapStatus === 'success';

export const getIsContractGenerated = (rootState: CustomerCardStoreState) => getSlice(rootState)?.isContractGenerated ?? false;

export const getIsDroppingCoBuyer = (rootState: CustomerCardStoreState) => getSlice(rootState)?.dropCoBuyerStatus === 'saving';

export const getDropCoBuyerFailed = (rootState: CustomerCardStoreState) => getSlice(rootState)?.dropCoBuyerStatus === 'error';

export const getDropCoBuyerSucceeded = (rootState: CustomerCardStoreState) => getSlice(rootState)?.dropCoBuyerStatus === 'success';

export const getDealId = (rootState: CustomerCardStoreState) => getSlice(rootState)?.dealId;

export const getDealerId = (rootState: CustomerCardStoreState) => getSlice(rootState)?.dealerId;

export const getInsuranceProviders = (rootState: CustomerCardStoreState) => getSlice(rootState)?.insuranceProviders ?? [];

export const getSaveInsuranceError = (rootState: CustomerCardStoreState) => getSlice(rootState)?.saveInsuranceError;

export const getVehicleInsuranceInfo = (rootState: CustomerCardStoreState) => getSlice(rootState)?.vehicleInsuranceInfo ?? null;

export const getIsVehicleInsuranceUpdated = (rootState: CustomerCardStoreState) =>
    getSlice(rootState)?.isVehicleInsuranceUpdated ?? false;

export const getContractApiErroredVehicleInsurance = (rootState: CustomerCardStoreState) =>
    getSlice(rootState)?.contractApiErroredVehicleInsurance ?? null;

export const getCommonOrgId = (rootState: CustomerCardStoreState) => getSlice(rootState)?.commonOrgId;

export const getCardOperation = (rootState: CustomerCardStoreState) => getSlice(rootState)?.operation ?? null;

export const getOperationKind = (rootState: CustomerCardStoreState) => getCardOperation(rootState)?.kind ?? null;

export const getOperationStep = (rootState: CustomerCardStoreState) => getCardOperation(rootState)?.step ?? null;

export const getOperationDraft = (rootState: CustomerCardStoreState) => getSlice(rootState)?.draft ?? null;

const getDraftForTarget = (rootState: CustomerCardStoreState, target: 'buyer' | 'coBuyer') => {
    const operation = getCardOperation(rootState);
    if (!operation || CARD_OPERATION_TARGET[operation.kind] !== target) return null;
    return getOperationDraft(rootState);
};

export const getBuyerDraft = (rootState: CustomerCardStoreState) => getDraftForTarget(rootState, 'buyer');

export const getCoBuyerDraft = (rootState: CustomerCardStoreState) => getDraftForTarget(rootState, 'coBuyer');

/** The participant the running operation acts on — what a draft replaces and the banner names. */
export const getOperationTargetParticipant = (rootState: CustomerCardStoreState) => {
    const operation = getCardOperation(rootState);
    if (!operation) return null;
    return CARD_OPERATION_TARGET[operation.kind] === 'coBuyer' ? getCoBuyer(rootState) : getBuyer(rootState) ?? null;
};

export const getSearchResults = (rootState: CustomerCardStoreState) => getSlice(rootState)?.search?.results ?? [];

const getBuyerCommonConsumerId = (rootState: CustomerCardStoreState) => getBuyer(rootState)?.commonConsumerId;

const getCoBuyerCommonConsumerId = (rootState: CustomerCardStoreState) => getCoBuyer(rootState)?.commonConsumerId;

/** Filters search results to exclude CCIDs already on the deal for change/add operations */
export const getVisibleSearchResults = createSelector(
    [getSearchResults, getOperationKind, getBuyerCommonConsumerId, getCoBuyerCommonConsumerId],
    (results, kind, buyerCommonConsumerId, coBuyerCommonConsumerId) => {
        if (!kind || !CARD_OPERATION_EXCLUDES_DEAL_PARTICIPANTS[kind]) return results;

        const attachedIds = [buyerCommonConsumerId, coBuyerCommonConsumerId].filter((id): id is string => !!id);
        if (!attachedIds.length) return results;

        return results.filter((result) => !attachedIds.includes(getSearchResultId(result)));
    }
);

export const getSearchLoading = (rootState: CustomerCardStoreState) => getSlice(rootState)?.search?.loading ?? false;

export const getSearchError = (rootState: CustomerCardStoreState) => getSlice(rootState)?.search?.error ?? null;

export const getSelectedSearchResult = (rootState: CustomerCardStoreState) => getSlice(rootState)?.search?.selected ?? null;

export const getSearchTerm = (rootState: CustomerCardStoreState) => getSlice(rootState)?.search?.term ?? null;

/** True on the search step of any operation — the card renders the search panel for all of them. */
export const getIsSearchStep = (rootState: CustomerCardStoreState) => getOperationStep(rootState) === CARD_OPERATION_STEP.SEARCH;

/** Picking any result is enough to continue, whichever entity the running operation searched. */
export const canContinueOperation = (rootState: CustomerCardStoreState) => !!getSelectedSearchResult(rootState);

export const getOperationOwnsCard = (rootState: CustomerCardStoreState) => {
    const operation = getCardOperation(rootState);
    return operation ? CARD_OPERATION_OWNS_CARD[operation.kind] : false;
};

/** True when an operation is running that the card outlives, so there is a step to go back to. */
export const getCanGoBack = (rootState: CustomerCardStoreState) =>
    Boolean(getCardOperation(rootState)) && !getOperationOwnsCard(rootState);

/**
 * A record with neither an id nor a surname is the empty shell a customer-less quote seeds. Exported
 * for hosts, whose own live offer state is a different source from the snapshot this card is seeded
 * with — the header must react before the next re-seed, so it shares the test, not the selector.
 */
export const isBuyerMissing = (commonConsumerId?: string, lastName?: string) => !commonConsumerId && !lastName;

export const getIsBuyerMissing = (rootState: CustomerCardStoreState) => {
    const buyer = getBuyer(rootState);
    return isBuyerMissing(buyer?.commonConsumerId, buyer?.lastName);
};
